import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  _SharedLinks,
  LinkStatus,
  SharedLink,
  SharedPlaylist,
  SharedPlaylistResponse,
} from 'types/shares.types';
import { GenericObject } from 'types/generic.types';
import { ShareResponse } from 'app/components/Shares/types';
import {
  createSharedLinkEffect,
  createSharedPlaylistLinkEffect,
  getSharedAudioListEffect,
  getSharedPlaylistsEffect,
  revokeSharedLinkEffect,
  revokeSharedPlaylistLinkEffect,
  updateSharedLinkEffect,
  updateSharedPlaylistLinkEffect,
} from './effects';

export interface SharesState {
  sharedAudioLinks: GenericObject<SharedLink[]>;
  sharedPlaylistLinks: GenericObject<SharedLink[]>;
  downloadList: string[];
  shareId?: string;
  sharedPlaylist?: SharedPlaylist;
  shareModal: {
    isOpen: boolean;
    type?: 'playlist' | 'audio';
  };
  _links?: _SharedLinks;
}

export const sharesInitialState: SharesState = {
  sharedAudioLinks: {},
  sharedPlaylistLinks: {},
  downloadList: [],
  shareModal: { isOpen: false },
};

const mergeLinksArray = (
  currentLinks: SharedLink[],
  newLinks: SharedLink[]
) => {
  const currentIds = (currentLinks || []).map((link) => link.guid);
  const newEntries = newLinks.filter((link) => !currentIds.includes(link.guid));
  return [...currentLinks, ...newEntries];
};

const slice = createSlice({
  name: 'shares',
  initialState: sharesInitialState,
  reducers: {
    addToDownloadList(state, { payload }: PayloadAction<string>) {
      state.downloadList.push(payload);
    },
    setShareModal(
      state,
      {
        payload,
      }: PayloadAction<{ type?: 'playlist' | 'audio'; isOpen: boolean }>
    ) {
      state.shareModal = payload;
    },
    removeFromDownloadList(state, { payload }: PayloadAction<string>) {
      const index = state.downloadList.findIndex((guid) => guid === payload);
      state.downloadList.splice(index, 1);
    },
    setShareId(state, { payload }: PayloadAction<string | undefined>) {
      state.shareId = payload;
    },
    setSharedAudio(state, { payload }: PayloadAction<ShareResponse>) {
      const {
        shareToken: guid,
        createdBy: {
          profileCard: { displayName: author },
        },
        _links: shareLinks,
      } = payload.response.share.item;

      const { referralId } = payload.response.config?.item || {};
      const {
        guid: audioGuid,
        title: audioTitle,
        duration,
        container,
        _links,
      } = payload.response.audio.item;

      const queue = [
        {
          songGuid: payload.response.song.item.guid || '',
          audioGuid,
          playlistAudioGuid: '',
          parentGuid: '',
          audioTitle,
          duration,
          note: '',
          songTitle: '',
          isMidi: container === 'midi',
        },
      ];

      state.sharedPlaylist = {
        guid,
        title: audioTitle || '',
        description: '',
        author,
        queue,
        _links: {
          ...shareLinks,
          ..._links,
        },
        referralId,
        images: [],
      };
    },
    setSharedPlaylist(
      state,
      { payload }: PayloadAction<SharedPlaylistResponse>
    ) {
      const {
        shareToken: guid,
        createdBy: {
          profileCard: { displayName: author },
        },
        _links: shareLinks,
      } = payload.response.share.item;

      const { referralId } = payload.response.config?.item || {};
      const { title, description, images } = payload.response.playlist.item;
      const { items, _links } = payload.response.playlistAudios;

      const queue = items.map((item) => ({
        playlistGuid: item.playlistGuid,
        audioGuid: item.audio.guid,
        playlistAudioGuid: item.guid,
        parentGuid: item.song.guid,
        audioTitle: item.title || item.audio.title,
        duration: item.audio.duration,
        note: item.note,
        songTitle: item.song.title,
        isMidi: item.audio.container === 'midi',
      }));

      state.sharedPlaylist = {
        guid,
        title,
        description,
        author,
        queue,
        _links: {
          ...shareLinks,
          ..._links,
        },
        referralId,
        images,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(
        getSharedAudioListEffect.fulfilled,
        (state, { payload, meta }) => {
          const { items, _links } = payload;
          const { guid, next } = meta.arg;
          const isNext = Boolean(next);
          state.sharedAudioLinks[guid] = isNext
            ? mergeLinksArray(state.sharedAudioLinks[guid], items)
            : items;
          state._links = _links;
        }
      )
      .addCase(createSharedLinkEffect.fulfilled, (state, { payload, meta }) => {
        state.sharedAudioLinks[meta.arg.audioGuid].splice(0, 0, payload);
      })
      .addCase(revokeSharedLinkEffect.fulfilled, (state, { meta }) => {
        const { linkGuid, guid } = meta.arg;
        const audioLink = state.sharedAudioLinks[guid].find(
          (link) => link.guid === linkGuid
        );
        if (audioLink) {
          audioLink.status = LinkStatus.REVOKED;
        }
      })
      .addCase(updateSharedLinkEffect.fulfilled, (state, { payload, meta }) => {
        const index = state.sharedAudioLinks[meta.arg.guid].findIndex(
          (link) => link.guid === payload.guid
        );
        state.sharedAudioLinks[meta.arg.guid][index] = payload;
      })
      .addCase(
        getSharedPlaylistsEffect.fulfilled,
        (state, { payload, meta }) => {
          const { items, _links } = payload;
          const { guid, next } = meta.arg;
          const isNext = Boolean(next);
          state.sharedPlaylistLinks[guid] = isNext
            ? mergeLinksArray(state.sharedPlaylistLinks[guid], items)
            : items;
          state._links = _links;
        }
      )
      .addCase(
        createSharedPlaylistLinkEffect.fulfilled,
        (state, { payload, meta }) => {
          state.sharedPlaylistLinks[meta.arg.playlistGuid].splice(
            0,
            0,
            payload
          );
        }
      )
      .addCase(revokeSharedPlaylistLinkEffect.fulfilled, (state, { meta }) => {
        const { linkGuid, guid } = meta.arg;
        const playlistLink = state.sharedPlaylistLinks[guid].find(
          (link) => link.guid === linkGuid
        );
        if (playlistLink) {
          playlistLink.status = LinkStatus.REVOKED;
        }
      })
      .addCase(
        updateSharedPlaylistLinkEffect.fulfilled,
        (state, { payload, meta }) => {
          const index = state.sharedPlaylistLinks[meta.arg.guid].findIndex(
            (link) => link.guid === meta.arg.linkGuid
          );
          state.sharedPlaylistLinks[meta.arg.guid][index] = payload;
        }
      );
  },
});

export const {
  addToDownloadList,
  removeFromDownloadList,
  setShareId,
  setSharedPlaylist,
  setSharedAudio,
  setShareModal,
} = slice.actions;

export const sharesReducer = slice.reducer;
